import type { Resultado } from './acesso';
import { CABECALHO_CSRF } from './auth';
import type { Sessao } from './auth';
import { buscar } from './buscar';
import type { RegrasDasFaltas } from './faltas';

/**
 * ENCOMENDAS — cliente HTTP das telas do fluxo (encomendar, minhas, todas, recebimento, revisar,
 * configurações, auditoria, sino). O servidor decide quem pode: a tela só mostra o que voltou.
 */

export async function chamar<T>(
  caminho: string,
  o: { metodo?: 'GET' | 'POST'; corpo?: unknown; sessao?: Sessao } = {},
): Promise<Resultado<T>> {
  const metodo = o.metodo ?? 'GET';
  let r: Response;
  try {
    r = await buscar(caminho, {
      method: metodo,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(metodo !== 'GET' ? { 'content-type': 'application/json' } : {}),
        ...(o.sessao ? { [CABECALHO_CSRF]: o.sessao.csrfToken } : {}),
      },
      ...(metodo !== 'GET' ? { body: JSON.stringify(o.corpo ?? {}) } : {}),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.status === 204) return { ok: true, dados: undefined as T };
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (r.status === 400 || r.status === 404)
    return { ok: false, falha: { tipo: 'invalido', mensagem: mensagem || 'Não encontrado.' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

/* ---------------- tipos ---------------- */

export type Macro =
  | 'CRIADA'
  | 'PEDIDO_EM_ANDAMENTO'
  | 'COMPRADA'
  | 'FATURADA'
  | 'A_CAMINHO'
  | 'RECEBIDA'
  | 'CONFERIDA'
  | 'PRONTA'
  | 'CONCLUIDA'
  | 'PROBLEMA'
  | 'CANCELADA';

export interface Situacao {
  readonly macro: Macro;
  readonly rotulo: string;
  readonly responsavel: string;
  readonly proximoPasso: string;
  readonly acaoDoBalconista: boolean;
  readonly progresso: number;
}

export interface Sla {
  readonly nivel: 'NORMAL' | 'ATENCAO' | 'ATRASADO';
  readonly paradoMin: number;
  readonly prazo: 'SEM_PRAZO' | 'NO_PRAZO' | 'PROXIMO' | 'VENCIDO';
}

export interface ItemDoResumo {
  readonly id: string;
  readonly descricao: string;
  readonly ean: string | null;
  readonly codigoHos: string | null;
  readonly quantidade: number;
  readonly estado: string;
  readonly comprada: number;
  readonly faturada: number;
  readonly chegou: number;
  readonly conferida: number;
  readonly controlado: boolean;
  readonly fornecedorConsultado: string | null;
  readonly fornecedorComprado: string | null;
}

export interface ResumoDaEncomenda {
  readonly id: string;
  readonly codigo: string;
  readonly criadoEm: string;
  readonly criadoPor: { readonly accountId: string; readonly nome: string };
  readonly cliente: { readonly nome: string; readonly telefone: string };
  readonly modalidade: 'RETIRADA' | 'ENTREGA';
  readonly urgente: boolean;
  readonly pagamento: string;
  readonly valorTotalCombinado: string | null;
  readonly valorPago: string;
  readonly prazoEm: string | null;
  readonly resolucao: string | null;
  readonly resolvidaEm: string | null;
  readonly itens: readonly ItemDoResumo[];
  readonly pedidos: readonly string[];
  readonly notas: readonly string[];
  readonly recebidaPor: string | null;
  readonly divergenciaAberta: boolean;
  readonly foto: string | null;
  readonly situacao: Situacao;
  readonly paradaDesde: string;
  readonly sla: Sla;
}

export interface PassoDaTimeline {
  readonly passo: string;
  readonly feito: boolean;
  readonly atual: boolean;
  readonly em: string | null;
  readonly detalhe: string | null;
}

export interface DetalheDaEncomenda {
  readonly id: string;
  readonly codigo: string;
  readonly criadoEm: string;
  readonly criadoPor: { readonly accountId: string; readonly nome: string };
  readonly cliente: {
    readonly nome: string;
    readonly telefone: string;
    readonly cpf: string | null;
  };
  readonly modalidade: 'RETIRADA' | 'ENTREGA';
  readonly endereco: Record<string, string | null> | null;
  readonly receita: { readonly situacao: string; readonly obs: string | null };
  readonly observacao: string | null;
  readonly urgente: boolean;
  readonly prazo: {
    readonly tipo: string;
    readonly em: string | null;
    readonly informadoPor: string | null;
  };
  readonly pagamento: {
    readonly situacao: string;
    readonly obs: string | null;
    readonly valorTotalCombinado: string | null;
    readonly pago: string;
    readonly saldo: string | null;
    readonly lancamentos: readonly {
      readonly id: string;
      readonly valor: string;
      readonly forma: string;
      readonly trocoPara: string | null;
      readonly registradoEm: string;
      readonly registradoPor: string;
      readonly estornado: boolean;
    }[];
  };
  readonly resolucao: {
    readonly como: string;
    readonly em: string;
    readonly por: string;
    readonly quemRecebeu: string | null;
    readonly obs: string | null;
  } | null;
  readonly itens: readonly {
    readonly id: string;
    readonly descricao: string;
    readonly ean: string | null;
    readonly codigoHos: string | null;
    readonly fabricante: string | null;
    readonly controlado: boolean;
    readonly quantidade: number;
    readonly estado: string;
    readonly fornecedorConsultado: string | null;
    readonly custoConsultado: string | null;
    readonly estoqueConsultado: number | null;
    readonly fornecedorComprado: string | null;
    readonly custoEfetivo: string | null;
    readonly pmc: string | null;
    readonly precoInova: string | null;
  }[];
  readonly situacao: Situacao;
  readonly timeline: readonly PassoDaTimeline[];
  readonly vinculos: {
    readonly pedidos: readonly {
      readonly id: string;
      readonly itemId: string;
      readonly pedido: string;
      readonly fornecedor: string | null;
      readonly quantidade: number;
      readonly comprador: string | null;
      readonly status: string | null;
      readonly confianca: string;
      readonly modo: string;
      readonly motivos: readonly string[];
      readonly desfeito: boolean;
    }[];
    readonly notas: readonly {
      readonly id: string;
      readonly itemId: string;
      readonly numero: string | null;
      readonly fornecedor: string | null;
      readonly quantidade: number;
      readonly fonte: string;
      readonly confianca: string;
      readonly modo: string;
      readonly motivos: readonly string[];
      readonly recebidaEm: string | null;
      readonly desfeito: boolean;
    }[];
  };
  readonly divergencias: readonly {
    readonly id: string;
    readonly itemId: string;
    readonly tipo: string;
    readonly esperada: number | null;
    readonly recebida: number | null;
    readonly obs: string | null;
    readonly abertaEm: string;
    readonly resolvida: boolean;
    readonly resolucao: string | null;
  }[];
  readonly eventos: readonly {
    readonly tipo: string;
    readonly em: string;
    readonly origem: string;
    readonly por: string | null;
    readonly dados: Record<string, unknown>;
  }[];
}

export interface NovaEncomenda {
  readonly cliente: {
    readonly nome: string;
    readonly telefone: string;
    readonly cpf?: string | null;
  };
  readonly modalidade: 'RETIRADA' | 'ENTREGA';
  readonly endereco?: {
    readonly cep: string | null;
    readonly logradouro: string;
    readonly numero: string;
    readonly complemento: string | null;
    readonly bairro: string;
    readonly referencia: string | null;
  } | null;
  readonly itens: readonly {
    readonly produtoId: string;
    readonly quantidade: number;
    readonly fornecedorSlug: string | null;
    readonly fornecedorNome: string | null;
  }[];
  readonly pagamento: {
    readonly situacao: string;
    readonly valorTotalCombinado?: string | null;
    readonly obs?: string | null;
    readonly recebido?: {
      readonly valor: string;
      readonly forma: string;
      readonly trocoPara?: string | null;
    } | null;
  };
  readonly receita: { readonly situacao: string; readonly obs?: string | null };
  readonly observacao?: string | null;
  readonly urgente?: boolean;
  readonly prazo: {
    readonly tipo: 'SEM_PRAZO' | 'DATA' | 'DATA_HORA';
    readonly em?: string | null;
  };
}

export interface NotaDoRecebimento {
  readonly chave: string;
  readonly numero: string | null;
  readonly fornecedor: string | null;
  readonly cnpj: string | null;
  readonly emissao: string | null;
  readonly valorTotalDaNota: number | null;
  readonly cancelada: boolean;
  readonly conferidaNoInovaped: boolean;
  readonly itens: readonly {
    readonly linha: string | null;
    readonly descricao: string | null;
    readonly ean: string | null;
    readonly codigoHos: string | null;
    readonly quantidade: number | null;
    readonly unidade: string | null;
  }[];
}

export interface ItemLigado {
  readonly vinculoId: string;
  readonly itemId: string;
  readonly quantidade: number;
  readonly descricao: string;
  readonly ean: string | null;
  readonly codigoHos: string | null;
  readonly encomendaId: string;
  readonly codigo: string;
  readonly cliente: string;
  readonly urgente: boolean;
  readonly estado: string;
  readonly conferido: { readonly quantidade: number; readonly resultado: string } | null;
}

export interface Revisao {
  readonly id: string;
  readonly tipo: 'PEDIDO' | 'NF';
  readonly razao: string;
  readonly score: number;
  readonly motivos: readonly string[];
  readonly candidato: Record<string, unknown>;
  readonly criadoEm: string;
  readonly item: {
    readonly id: string;
    readonly descricao: string;
    readonly quantidade: number;
    readonly faltando: number;
    readonly encomendaId: string;
    readonly codigo: string;
    readonly cliente: string;
    readonly fornecedorConsultado: string | null;
  };
}

export interface Notificacao {
  readonly id: string;
  readonly encomendaId: string | null;
  readonly tipo: string;
  readonly titulo: string;
  readonly texto: string;
  readonly criadaEm: string;
  readonly lida: boolean;
}

export interface ConfigDasEncomendas {
  readonly sla: {
    readonly normal: { readonly atencaoMin: number; readonly atrasoMin: number };
    readonly urgente: { readonly atencaoMin: number; readonly atrasoMin: number };
  };
  readonly disponibilidade: { readonly cacheSegundos: number; readonly velhaDepoisMin: number };
  readonly sincronizacao: { readonly ligada: boolean; readonly minutos: number };
  readonly regras: {
    readonly receitaObrigatoriaNoControlado: boolean;
    readonly prazoObrigatorio: boolean;
  };
  readonly exibicao: { readonly mostrarPmc: boolean };
  readonly distribuidoras: Readonly<
    Record<string, { readonly ativa: boolean; readonly nome: string | null }>
  >;
  /** Registrar falta (0061): antiabuso da campanha e os limites dos alertas. */
  readonly faltas: RegrasDasFaltas;
}

export interface Integracoes {
  readonly fontes: readonly {
    readonly nome: string;
    readonly uso: string;
    readonly ok: boolean;
    readonly mensagem: string | null;
    readonly latenciaMs: number;
  }[];
  readonly distribuidoras: readonly {
    readonly nome: string;
    readonly slug: string | null;
    readonly estado: string;
    readonly horas: number | null;
  }[];
  readonly execucoes: readonly {
    readonly iniciadaEm: string;
    readonly terminadaEm: string | null;
    readonly origem: string;
    readonly itensVistos: number;
    readonly vinculos: number;
    readonly revisar: number;
    readonly erros: readonly string[];
  }[];
}

export interface Painel {
  readonly indicadores: Record<
    | 'hoje'
    | 'abertas'
    | 'aguardandoPedido'
    | 'compradas'
    | 'faturadas'
    | 'aCaminho'
    | 'chegaram'
    | 'conferidas'
    | 'prontas'
    | 'atrasadas'
    | 'comProblema'
    | 'resolvidasHoje',
    number
  >;
  readonly atencao: readonly {
    readonly chave: string;
    readonly quantidade: number;
    readonly frase: string;
    readonly filtro: string;
    readonly tom: 'atencao' | 'problema';
  }[];
}

/* ---------------- chamadas ---------------- */

export const criarEncomenda = (s: Sessao, corpo: NovaEncomenda) =>
  chamar<{ id: string; codigo: string }>('/api/encomendas', { metodo: 'POST', corpo, sessao: s });
export const lerMinhas = () =>
  chamar<{ encomendas: readonly ResumoDaEncomenda[] }>('/api/encomendas/minhas');
export const lerTodas = () =>
  chamar<{ encomendas: readonly ResumoDaEncomenda[] }>('/api/encomendas');
export const lerPainel = () => chamar<Painel>('/api/encomendas/painel');
export const lerEncomenda = (id: string) =>
  chamar<{ encomenda: DetalheDaEncomenda }>(`/api/encomendas/${id}`);
export const resolver = (
  s: Sessao,
  id: string,
  corpo: { como: string; quemRecebeu?: string | null; obs?: string | null },
) => chamar<undefined>(`/api/encomendas/${id}/resolver`, { metodo: 'POST', corpo, sessao: s });
export const pagar = (
  s: Sessao,
  id: string,
  corpo: { valor: string; forma: string; trocoPara?: string | null },
) =>
  chamar<{ pago: string; saldo: string | null }>(`/api/encomendas/${id}/pagamentos`, {
    metodo: 'POST',
    corpo,
    sessao: s,
  });
export const lerRevisoes = () =>
  chamar<{ revisoes: readonly Revisao[] }>('/api/encomendas/revisar');
export const decidir = (
  s: Sessao,
  id: string,
  corpo: { decisao: 'CONFIRMADO' | 'DESCARTADO'; quantidade?: number | null; obs?: string | null },
) => chamar<undefined>(`/api/encomendas/revisar/${id}`, { metodo: 'POST', corpo, sessao: s });
export const desfazer = (s: Sessao, tipo: 'pedido' | 'nf', id: string, motivo: string) =>
  chamar<undefined>(`/api/encomendas/vinculos/${tipo}/${id}/desfazer`, {
    metodo: 'POST',
    corpo: { motivo },
    sessao: s,
  });
export const resolverDivergencia = (s: Sessao, id: string, acao: string, resolucao: string) =>
  chamar<undefined>(`/api/encomendas/divergencias/${id}/resolver`, {
    metodo: 'POST',
    corpo: { acao, resolucao },
    sessao: s,
  });
export const sincronizarAgora = (s: Sessao) =>
  chamar<{ itensVistos: number; vinculos: number; revisar: number; erros: readonly string[] }>(
    '/api/encomendas/sincronizar',
    { metodo: 'POST', sessao: s },
  );
export const procurarNota = (q: { chave?: string; q?: string }) =>
  chamar<{
    fonte: { ok: boolean; mensagem?: string };
    nota?: NotaDoRecebimento | null;
    notas?: readonly {
      chave: string;
      numero: string | number | null;
      fornecedor: string | null;
      emissao: string | null;
    }[];
    ligados?: readonly ItemLigado[];
    recebimento?: { id: string; recebidoEm: string; recebidoPor: string } | null;
  }>(
    `/api/encomendas/recebimento/nota?${q.chave ? `chave=${q.chave}` : `q=${encodeURIComponent(q.q ?? '')}`}`,
  );
export const confirmarChegada = (s: Sessao, chave: string, obs: string | null) =>
  chamar<{ recebimentoId: string; jaEstava: boolean; itens: number }>(
    '/api/encomendas/recebimento/chegou',
    { metodo: 'POST', corpo: { chave, obs }, sessao: s },
  );
export const conferir = (
  s: Sessao,
  recebimentoId: string,
  itens: readonly {
    itemId: string;
    quantidadeRecebida: number;
    resultado?: string | null;
    obs?: string | null;
  }[],
) =>
  chamar<{ conferidos: number; divergencias: number }>(
    `/api/encomendas/recebimento/${recebimentoId}/conferir`,
    { metodo: 'POST', corpo: { itens }, sessao: s },
  );
export const conferirTudo = (s: Sessao, recebimentoId: string) =>
  chamar<{ conferidos: number; divergencias: number }>(
    `/api/encomendas/recebimento/${recebimentoId}/conferir-tudo`,
    { metodo: 'POST', sessao: s },
  );
export const meusRecebimentos = () =>
  chamar<{
    recebimentos: readonly {
      id: string;
      numero: string | null;
      chave: string | null;
      fornecedor: string | null;
      recebidoEm: string;
      itensDeEncomenda: number;
    }[];
  }>('/api/encomendas/recebimento/meus');
export const lerNotificacoes = () =>
  chamar<{ naoLidas: number; notificacoes: readonly Notificacao[] }>(
    '/api/encomendas/notificacoes',
  );
export const marcarLidas = (s: Sessao, ids: readonly string[] | null) =>
  chamar<{ marcadas: number }>('/api/encomendas/notificacoes/lidas', {
    metodo: 'POST',
    corpo: { ids },
    sessao: s,
  });
export const lerConfig = () => chamar<{ config: ConfigDasEncomendas }>('/api/encomendas/config');
export const salvarConfig = (s: Sessao, chave: keyof ConfigDasEncomendas, valor: unknown) =>
  chamar<{ config: ConfigDasEncomendas }>('/api/encomendas/config', {
    metodo: 'POST',
    corpo: { chave, valor },
    sessao: s,
  });
export const lerIntegracoes = () => chamar<Integracoes>('/api/encomendas/integracoes');
export const lerAuditoria = (encomendaId?: string) =>
  chamar<{
    eventos: readonly {
      id: string;
      tipo: string;
      codigo: string | null;
      em: string;
      por: string;
      origem: string;
      metadados: Record<string, unknown>;
    }[];
  }>(`/api/encomendas/auditoria${encomendaId ? `?encomenda=${encomendaId}` : ''}`);

/* ---------------- textos ---------------- */

export const ROTULO_PAGAMENTO: Readonly<Record<string, string>> = {
  NAO_PAGO: 'Não pago',
  PAGO: 'Pago',
  SINAL: 'Sinal',
  PAGAR_NA_RETIRADA: 'Paga na retirada',
  PAGAR_NA_ENTREGA: 'Paga na entrega',
  OUTRO: 'Outro',
};
export const ROTULO_RECEITA: Readonly<Record<string, string>> = {
  NAO_NECESSITA: 'Não necessita',
  FICOU_NA_LOJA: 'Receita ficou na loja',
  TRAZ_NA_RETIRADA: 'Cliente trará na retirada',
  ENVIOU_DIGITAL: 'Cliente enviou digital',
  OUTRO: 'Outro',
};
export const ROTULO_RESOLUCAO: Readonly<Record<string, string>> = {
  RETIRADA: 'Retirada pelo cliente',
  ENTREGUE: 'Entregue no endereço',
  DESISTIU: 'Cliente desistiu',
  CANCELADA: 'Cancelada',
  OUTRO: 'Outro',
};
export const ROTULO_RAZAO: Readonly<Record<string, string>> = {
  PROVAVEL: 'Vínculo provável',
  AMBIGUO: 'Ambíguo (mais de um candidato)',
  DUAS_ENCOMENDAS: 'Duas encomendas disputam',
  PARCIAL: 'Parcial',
  OUTRO_FORNECEDOR: 'Outro fornecedor',
  QUANTIDADE_MENOR: 'Quantidade menor',
};

/** "há 3 h" — tempo parado. */
export function tempoParado(min: number): string {
  if (min < 60) return `${String(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${String(h)} h`;
  return `${String(Math.floor(h / 24))} dias`;
}

export function dataHoraBr(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function telefoneBr(t: string): string {
  const d = t.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return t;
}
